'use client';

import React from 'react';
import * as Sentry from 'utils/errorTracking';

// Components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@startengine/design-system.ui.button';
import { styled } from '@mui/material/styles';
import Image from 'next/legacy/image';

// Utils
import { imagePath } from 'utils/helpers';

// Styled Components following the established pattern
const ErrorContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '60vh',
  padding: '60px 20px',
  margin: '0 auto',
  maxWidth: '600px',
}));

const ErrorTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '28px',
  lineHeight: '34px',
  textAlign: 'center',
  letterSpacing: '0.05px',
  color: theme.palette.secondary.main,
  marginBottom: '16px',
  [theme.breakpoints.down('md')]: {
    fontSize: '24px',
    lineHeight: '30px',
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '18px',
  lineHeight: '22px',
  textAlign: 'center',
  letterSpacing: '0.05px',
  color: theme.palette.common.tabsTextColor,
  marginBottom: '32px',
  [theme.breakpoints.down('md')]: {
    fontSize: '16px',
    lineHeight: '20px',
    marginBottom: '24px',
  },
}));

class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: null,
    };
  }

  static getDerivedStateFromError(_error) {
    // Generate a unique error ID for user reference
    const errorId = `PAGE-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      errorId,
    };
  }

  componentDidCatch(error, errorInfo) {
    const { pathname } = this.props;
    const { errorId } = this.state;

    // Log the error to Sentry with page context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
        page: {
          pathname: pathname || 'unknown',
          errorBoundary: 'PageErrorBoundary',
          level: 'page',
        },
      },
      extra: {
        errorId,
        message: 'Page-level error caught by PageErrorBoundary',
        props: this.props,
      },
      tags: {
        errorBoundary: 'page',
        errorId,
      },
    });

    // Log for debugging (only in development)
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[PageErrorBoundary] Page-level error:', {
        error: error.message,
        errorId,
        pathname,
        stack: error.stack,
      });
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, errorId: null });
  };

  handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  render() {
    const { hasError, errorId } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <ErrorContainer data-cy="page-error-boundary" data-testid="page-error-boundary">
          <Box sx={{ mb: 3 }}>
            <Image
              src={`${imagePath}/images/icons/lightbulb-icon.svg`}
              alt="Page Error"
              height={80}
              width={80}
              style={{ opacity: 0.5 }}
            />
          </Box>

          <ErrorTitle>This page isn&apos;t working right now</ErrorTitle>

          <ErrorMessage>
            Something went wrong while loading this page. You can try refreshing, go back to the
            homepage, or browse our investments.
          </ErrorMessage>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 3 }}>
            <Button
              variant="contained"
              onClick={this.handleReload}
              sx={{
                height: '44px',
                minWidth: '140px',
                backgroundColor: 'black',
                '&:hover': {
                  backgroundColor: '#333',
                },
              }}
              data-cy="page-reload-button"
              data-testid="page-reload-button"
            >
              Refresh Page
            </Button>

            <Button
              variant="outlined"
              onClick={this.handleGoHome}
              sx={{
                height: '44px',
                minWidth: '140px',
                borderColor: 'black',
                color: 'black',
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
              data-cy="go-home-button"
              data-testid="go-home-button"
            >
              Go Home
            </Button>

            <Button
              variant="text"
              onClick={this.handleRetry}
              sx={{
                height: '44px',
                minWidth: '100px',
                color: 'grey.600',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
              data-cy="page-retry-button"
              data-testid="page-retry-button"
            >
              Try Again
            </Button>
          </Box>

          {/* Error ID for support */}
          {errorId && (
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                fontSize: '12px',
                textAlign: 'center',
                fontFamily: 'monospace',
              }}
            >
              Error ID: {errorId}
            </Typography>
          )}
        </ErrorContainer>
      );
    }

    return children;
  }
}

export default PageErrorBoundary;
